import SportIcon from '@/components/SportIcon';
/**
 * ScorerSportPage.jsx
 * Score a specific sport within an encounter.
 * Route: /score/enc/:encId/sport/:sportCode
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Play, Square, Clock, Check, Edit2, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const SPORT_LABELS = {
  table_tennis:'Table Tennis', badminton:'Badminton', chess:'Chess',
  carrom:'Carrom', pickleball:'Pickleball', squash:'Squash',
};
const SPORT_ICONS = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾',
};
const SPORT_SCORING = {
  table_tennis: { game_points:11, score_type:'points', label:'11 pts/game', singles_count:6, doubles_count:0 },
  badminton:    { game_points:21, score_type:'points', label:'21 pts/game', singles_count:0, doubles_count:3 },
  pickleball:   { game_points:15, score_type:'points', label:'15 pts/game', singles_count:0, doubles_count:5 },
  chess:        { game_points:0,  score_type:'result', label:'Win/Draw/Loss', singles_count:3, doubles_count:0 },
  carrom:       { game_points:0,  score_type:'result', label:'Win/Draw/Loss', singles_count:0, doubles_count:5 },
};
const SLOT_COLOR = { P:'#f5c518', R:'#3b82f6' };

const fmtTime = (iso) => iso
  ? new Date(iso).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true,timeZone:'Asia/Kolkata'})
  : '';

export default function ScorerSportPage() {
  const { encId, sportCode } = useParams();
  const navigate = useNavigate();
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token = () => localStorage.getItem('btccp_access_token');

  const location = useLocation();
  // Single-match mode: focusTie is the index of the match to score
  const [focusTie, setFocusTie] = useState(location.state?.focusTie ?? null);
  const singleMatchMode = focusTie !== null;

  const [enc, setEnc]           = useState(null);
  const [sportResult, setSportResult] = useState(null);
  const [rosters, setRosters]   = useState({ home:[], away:[] });
  const [sportFmt, setSportFmt] = useState(null);
  const [dirtyMatches, setDirtyMatches] = useState({});
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);
  const [editingTime, setEditingTime] = useState(null);
  const [editTimeVal, setEditTimeVal] = useState('');
  const [matchSaved, setMatchSaved] = useState(false); // single-match result overlay
  const [bonusGame, setBonusGame]   = useState(null);  // TT golden game: {winner:'home'|'away'} or null
  const [bonusGameDirty, setBonusGameDirty] = useState(false);
  const isGoldenGame = location.state?.goldenGame === true;

  useEffect(() => { fetchData(); }, [encId, sportCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const listRes = await fetch(`/api/orgs/${orgSlug}/scorer/encounters`,
        { headers: { 'Authorization': `Bearer ${token()}` } });
      const list = listRes.ok ? await listRes.json() : [];
      const found = list.find(e => e.id === encId);
      if (!found) { toast.error('Match not found'); navigate('/score'); return; }
      setEnc(found);
      const sr = found.sport_results?.find(s => s.sport_code === sportCode);
      if (!sr) { toast.error(`Sport ${sportCode} not in this encounter`); navigate('/score'); return; }
      setSportResult(sr);
      setBonusGame(sr.bonus_game || null);
      setBonusGameDirty(false);
      // Fetch rosters
      const rRes = await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${encId}/rosters`,
        { headers: { 'Authorization': `Bearer ${token()}` } });
      if (rRes.ok) setRosters(await rRes.json());
      // Fetch sport formats
      if (found.tournament_id) {
        const fmtRes = await fetch(`/api/orgs/${orgSlug}/tournaments/${found.tournament_id}/sport-formats`,
          { headers: { 'Authorization': `Bearer ${token()}` } });
        if (fmtRes.ok) {
          const fmts = await fmtRes.json();
          const fmt = (Array.isArray(fmts)?fmts:[]).find(f => f.sport_code === sportCode);
          if (fmt) setSportFmt(fmt);
        }
      }
    } catch (_) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  // How many games per match (3 for league TT, 5 for KO TT)
  const isKO      = enc && ['sf','final','3rd'].includes(enc.round_type);
  const gamesPerMatch = sportFmt
    ? (isKO ? (sportFmt.ko_games_per_match || sportFmt.games_per_match || 1) : (sportFmt.games_per_match || 1))
    : 1;
  const needsGames = gamesPerMatch > 1;  // true for TT (3 or 5 games)
  const toWinGames = Math.ceil(gamesPerMatch / 2);

  // Update a specific game score within a match
  const updateGame = (matchIdx, gameIdx, side, score) => {
    setDirtyMatches(prev => {
      const key = matchIdx;
      const existing = prev[key] || {};
      const games = [...(existing.games || sportResult?.matches?.[matchIdx]?.games || [])];
      while (games.length <= gameIdx) games.push({ home: 0, away: 0 });
      games[gameIdx] = { ...games[gameIdx], [side]: parseInt(score)||0 };

      // Recalculate rubber winner from games
      const gp      = sportFmt?.game_points ?? 11;
      const minDiff = sportFmt?.min_diff ?? 2;
      let hw = 0, aw = 0;
      games.forEach(g => {
        const hs = g.home||0, as_ = g.away||0;
        const winTarget = Math.max(gp, Math.max(hs, as_));
        if (hs >= winTarget && hs - as_ >= minDiff) hw++;
        else if (as_ >= winTarget && as_ - hs >= minDiff) aw++;
      });

      const updated = { ...existing, games, home_score: hw, away_score: aw };
      if (hw >= toWinGames)       { updated.winner = 'home'; updated.status = 'completed'; }
      else if (aw >= toWinGames)  { updated.winner = 'away'; updated.status = 'completed'; }
      else                        { updated.winner = null; delete updated.status; }
      return { ...prev, [key]: updated };
    });
  };

  const updateField = (idx, field, value) =>
    setDirtyMatches(prev => {
      const existing = prev[idx] || {};
      const updated = { ...existing, [field]: value };

      // ── Auto-detect winner for single-score numeric sports ──────────────
      // (Pickleball=15, Badminton=21, Squash=11 — NOT TT multi-game, NOT Chess/Carrom)
      if ((field === 'home_score' || field === 'away_score') && !needsGames) {
        const sportDef_ = SPORT_SCORING[sportCode];
        if (sportDef_?.score_type === 'points') {
          const hs  = parseInt(field === 'home_score' ? value
                               : (existing.home_score ?? sportResult?.matches?.[idx]?.home_score ?? 0)) || 0;
          const as_ = parseInt(field === 'away_score' ? value
                               : (existing.away_score ?? sportResult?.matches?.[idx]?.away_score ?? 0)) || 0;
          const gp       = sportFmt?.game_points ?? sportDef_?.game_points ?? 11;
          const minDiff  = sportFmt?.min_diff ?? 2;
          if      (hs >= gp && hs - as_ >= minDiff)  updated.winner = 'home';
          else if (as_ >= gp && as_ - hs >= minDiff) updated.winner = 'away';
          else                                         delete updated.winner;
        }
      }

      return { ...prev, [idx]: updated };
    });

  const getMatchValue = (idx, field, fallback) =>
    dirtyMatches[idx]?.[field] !== undefined ? dirtyMatches[idx][field] : (sportResult?.matches?.[idx]?.[field] ?? fallback);

  const getPlayers = (side) => {
    const list = rosters[side] || [];
    return list.filter(p =>
      !p.sports?.length || p.sports.includes(sportCode)
    );
  };
  const getPlatinumPlayers = (side) => getPlayers(side).filter(p =>
    p.platinum_sports?.includes(sportCode) || p.is_platinum
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      // Merge dirty into matches
      const matches = (sportResult?.matches || []).map((m, i) => {
        const d = dirtyMatches[i];
        if (!d) return m;
        const merged = { ...m, ...d };
        if (d.home_score !== undefined) merged.home_score = parseInt(d.home_score)||0;
        if (d.away_score !== undefined) merged.away_score = parseInt(d.away_score)||0;
        return merged;
      });
      const res = await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${encId}/sport/${sportCode}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ matches, ...(bonusGame ? { bonus_game: bonusGame } : {}) }),
      });
      if (res.ok) {
        toast.success('Scores saved');
        setDirtyMatches({});
        setBonusGameDirty(false);
        fetchData();
      } else { const d=await res.json(); toast.error(d.detail||'Failed'); }
    } catch (_) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const [showResult, setShowResult] = useState(false);

  // ── Single-match save (used in focusTie mode) ─────────────────────────────
  const handleSingleMatchSave = async () => {
    setSaving(true);
    try {
      // Auto-start encounter if needed
      if (enc.status !== 'in_progress' && enc.tournament_id) {
        await fetch(`/api/orgs/${orgSlug}/tournaments/${enc.tournament_id}/encounters/${encId}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'in_progress' }),
        });
        setEnc(prev => ({ ...prev, status: 'in_progress' }));
      }
      // Merge dirty into all matches (preserving untouched ones)
      const matches = (sportResult?.matches || []).map((m, i) => {
        const d = dirtyMatches[i]; if (!d) return m;
        const merged = { ...m, ...d };
        if (d.home_score !== undefined) merged.home_score = parseInt(d.home_score) || 0;
        if (d.away_score !== undefined) merged.away_score = parseInt(d.away_score) || 0;
        return merged;
      });
      const res = await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${encId}/sport/${sportCode}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ matches, ...(bonusGame ? { bonus_game: bonusGame } : {}) }),
      });
      if (res.ok) {
        toast.success('Match saved!');
        setDirtyMatches({});
        setBonusGameDirty(false);
        await fetchData();
        setMatchSaved(true);
      } else { const d = await res.json(); toast.error(d.detail || 'Failed to save'); }
    } catch (_) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // ── Golden Game save ─────────────────────────────────────────────────────
  const handleGoldenGameSave = async () => {
    if (!bonusGame?.winner) return;
    setSaving(true);
    try {
      const matches = sportResult?.matches || [];
      const res = await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${encId}/sport/${sportCode}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ matches, bonus_game: bonusGame }),
      });
      if (res.ok) {
        toast.success('Golden game saved!');
        setBonusGameDirty(false);
        await fetchData();
        setMatchSaved(true);
      } else { const d = await res.json(); toast.error(d.detail || 'Failed to save'); }
    } catch (_) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleStartMatch = async () => {
    try {
      await recordTime('start_time');
      await fetch(`/api/orgs/${orgSlug}/tournaments/${enc.tournament_id}/encounters/${encId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'in_progress' }),
      });
      setEnc(prev => ({ ...prev, status: 'in_progress', start_time: prev.start_time || new Date().toISOString() }));
      toast.success('Match started!');
    } catch (_) { toast.error('Failed to start'); }
  };

  const handleEndMatch = async () => {
    setSaving(true);
    try {
      // 1. Save all scores
      const matches = (sportResult?.matches || []).map((m, i) => {
        const d = dirtyMatches[i]; if (!d) return m;
        const merged = { ...m, ...d };
        if (d.home_score !== undefined) merged.home_score = parseInt(d.home_score)||0;
        if (d.away_score !== undefined) merged.away_score = parseInt(d.away_score)||0;
        return merged;
      });
      const saveRes = await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${encId}/sport/${sportCode}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ matches, ...(bonusGame ? { bonus_game: bonusGame } : {}) }),
      });
      if (!saveRes.ok) { const d=await saveRes.json(); toast.error(d.detail||'Save failed'); return; }
      // 2. Record end time
      await recordTime('end_time');
      // 3. Refresh and show result
      setDirtyMatches({});
      await fetchData();
      setShowResult(true);
    } catch (_) { toast.error('Failed to end match'); }
    finally { setSaving(false); }
  };

  const recordTime = async (field) => {
    const now = new Date().toISOString();
    try {
      await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${encId}/time`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: now }),
      });
      setEnc(prev => ({ ...prev, [field]: now }));
    } catch (_) {}
  };
  const localTimeToISO = (t) => { const [h,m]=t.split(':').map(Number); const d=new Date(); d.setHours(h,m,0,0); return d.toISOString(); };
  const saveEditedTime = async (field) => {
    if (!editTimeVal) { setEditingTime(null); return; }
    try {
      const iso = localTimeToISO(editTimeVal);
      await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${encId}/time`, {
        method:'PATCH', headers:{'Authorization':`Bearer ${token()}`,'Content-Type':'application/json'},
        body: JSON.stringify({ [field]: iso }),
      });
      setEnc(prev=>({...prev,[field]:iso})); toast.success('Time updated');
    } catch (_) { toast.error('Failed'); }
    setEditingTime(null);
  };

  if (loading) return <div className="p-4 space-y-3">{[1,2,3].map(i=><Skeleton key={i} className="h-24 bg-[#1d2230] rounded-xl"/>)}</div>;
  if (!enc || !sportResult) return null;

  const isLive  = enc.status === 'in_progress';
  const isDone  = enc.status === 'completed';
  const sportDef = SPORT_SCORING[sportCode] || { game_points:11, score_type:'points', label:'pts/game' };
  const isResult = sportDef.score_type === 'result';
  const homeLabel = enc.home_team?.name || enc.home_seed || 'Home';
  const awayLabel = enc.away_team?.name || enc.away_seed || 'Away';
  const homeColor = enc.home_team?.color || '#3b82f6';
  const awayColor = enc.away_team?.color || '#ef4444';
  const hasDirty = Object.keys(dirtyMatches).some(k => !k.startsWith('sub_')) || bonusGameDirty;
  const allTiesDone = (sportResult.matches||[]).every(m => m.winner);
  const sportWinner = sportResult.sport_winner;

  // ── Golden game computed (Table Tennis tiebreaker) ────────────────────────
  const mergedForGolden = (sportResult?.matches || []).map((m, i) =>
    dirtyMatches[i] ? { ...m, ...dirtyMatches[i] } : m
  );
  const cgHomeWins = mergedForGolden.filter(m => m.winner && ['home','bye_home'].includes(m.winner)).length;
  const cgAwayWins = mergedForGolden.filter(m => m.winner && ['away','bye_away'].includes(m.winner)).length;
  const allRegularDone = mergedForGolden.length > 0 && mergedForGolden.every(m => m.winner);
  // Show golden game card in full mode when TT, all regular matches done, and tied
  const showGoldenGameCard = !isGoldenGame && !singleMatchMode &&
    sportCode === 'table_tennis' && allRegularDone && cgHomeWins === cgAwayWins;

  // ── GOLDEN GAME: single-match mode entry via { goldenGame: true } ──────────
  if (isGoldenGame && !matchSaved) {
    return (
      <div className="p-4 sm:p-6 pb-28">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-[#8a93a3] -ml-2">
          <ArrowLeft className="h-4 w-4 mr-2"/>Back to Tie
        </Button>
        <div className="flex items-center gap-3 mb-6">
          <SportIcon sport={sportCode} size={32}/>
          <div>
            <h1 className="text-xl font-black text-[#f0f2f7]">{SPORT_LABELS[sportCode]||sportCode}</h1>
            <p className="text-xs text-[#8a93a3]">
              <span style={{color:homeColor}}>{homeLabel}</span>{' vs '}
              <span style={{color:awayColor}}>{awayLabel}</span>
            </p>
          </div>
          <div className="ml-auto">
            <span className="bg-[rgba(245,197,24,0.12)] border border-[rgba(245,197,24,0.45)] text-[#f5c518] font-black text-sm px-3 py-1.5 rounded-lg whitespace-nowrap">
              ⚡ GOLDEN GAME
            </span>
          </div>
        </div>

        <Card className="border-2 border-[rgba(245,197,24,0.4)] bg-[rgba(245,197,24,0.03)] mb-4">
          <CardContent className="p-5">
            <div className="text-center mb-4">
              <p className="text-[#f5c518] font-black text-lg mb-1">
                {sportResult?.home_match_wins ?? 0} – {sportResult?.away_match_wins ?? 0} Tied
              </p>
              <p className="text-xs text-[#8a93a3]">Regular matches are equal. Play one bonus deciding game.</p>
              <p className="text-xs font-semibold text-[#f5c518] mt-1 bg-[rgba(245,197,24,0.08)] px-3 py-1 rounded-full inline-block">
                No points awarded · Decides tie winner only
              </p>
            </div>
            <div className="flex gap-2">
              {[
                { side:'home', label:`${homeLabel} Wins`, color:'#22c55e' },
                { side:'away', label:`${awayLabel} Wins`, color:'#ef4444' },
              ].map(opt => (
                <button key={opt.side}
                  onClick={() => { setBonusGame({ winner: opt.side }); setBonusGameDirty(true); }}
                  className={`flex-1 py-3 rounded-xl text-sm font-bold border transition-all ${
                    bonusGame?.winner === opt.side
                      ? 'border-current text-current bg-current/10 scale-[1.01]'
                      : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:text-current hover:border-current'
                  }`}
                  style={{ color: bonusGame?.winner===opt.side ? opt.color : undefined,
                           borderColor: bonusGame?.winner===opt.side ? opt.color : undefined }}>
                  {opt.label}
                </button>
              ))}
            </div>
            {bonusGame?.winner && (
              <div className="mt-3 text-center py-2.5 rounded-xl bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.25)]">
                <p className="text-[#22c55e] font-black text-sm">
                  ⚡ {bonusGame.winner==='home' ? homeLabel : awayLabel} wins the tie!
                </p>
                <p className="text-[10px] text-[#8a93a3] mt-0.5">No extra points — tie winner decided</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#10131c]/95 backdrop-blur border-t border-[#252a37] p-4">
          <Button onClick={handleGoldenGameSave} disabled={saving || !bonusGame?.winner}
            className={`w-full h-14 font-black text-base ${
              bonusGame?.winner ? 'bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]' : 'bg-[#1d2230] text-[#8a93a3]'
            }`}>
            {saving ? 'Saving...' : bonusGame?.winner ? 'Save Golden Game Result' : 'Select a winner to continue'}
          </Button>
        </div>
      </div>
    );
  }

  // ── GOLDEN GAME result overlay ────────────────────────────────────────────
  if (isGoldenGame && matchSaved) {
    const bgWinner = bonusGame?.winner === 'home' ? homeLabel : awayLabel;
    const bgColor  = bonusGame?.winner === 'home' ? homeColor  : awayColor;
    return (
      <div className="fixed inset-0 bg-[#06080d] flex flex-col items-center justify-center p-6 z-50">
        <div className="text-center max-w-sm">
          <div className="text-6xl mb-4">⚡</div>
          <p className="text-xs text-[#f5c518] uppercase tracking-widest font-bold mb-2">Golden Game</p>
          <p className="text-3xl font-black mb-1" style={{ color: bgColor }}>{bgWinner} wins the tie!</p>
          <p className="text-sm text-[#8a93a3] mb-1">No extra points · Tie decided by golden game</p>
          <p className="text-xs text-[#22c55e] font-semibold mb-8">✓ Result saved · Scoreboard updated</p>
          <Button onClick={() => navigate('/score')}
            className="w-full h-12 bg-[#f5c518] text-[#0b0f18] font-black text-base">
            Back to All Matches
          </Button>
        </div>
      </div>
    );
  }

  // ── Single-match result overlay ────────────────────────────────────────────
  if (singleMatchMode && matchSaved) {
    const m = sportResult?.matches?.[focusTie];
    const mWinner = m?.winner;
    const isResultSport = ['chess', 'carrom'].includes(sportCode);
    const nextIdx = focusTie + 1;
    const hasNext = nextIdx < (sportResult?.matches?.length || 0);
    const winnerText =
      mWinner === 'home' ? `${homeLabel} wins!` :
      mWinner === 'away' ? `${awayLabel} wins!` :
      mWinner === 'draw' ? 'Draw!' :
      mWinner?.startsWith('bye') ? 'BYE awarded' :
      'Result saved';
    const winnerColor =
      mWinner === 'home' ? '#22c55e' :
      mWinner === 'away' ? homeColor :
      mWinner === 'draw' ? '#f5c518' : '#8a93a3';

    return (
      <div className="p-4 sm:p-6 flex flex-col items-center justify-center min-h-[70vh]">
        <div className="text-center max-w-sm w-full">
          {/* Icon ring */}
          <div className="w-24 h-24 rounded-full bg-[rgba(34,197,94,0.08)] border-2 border-[rgba(34,197,94,0.35)] flex items-center justify-center mx-auto mb-6">
            <SportIcon sport={sportCode} size={48}/>
          </div>

          <p className="text-xs text-[#8a93a3] uppercase tracking-widest mb-1">
            Match {focusTie + 1} · {SPORT_LABELS[sportCode]}
          </p>

          <p className="text-3xl font-black mb-1" style={{ color: winnerColor }}>
            {winnerText}
          </p>

          {!isResultSport && mWinner && !mWinner.startsWith('bye') && mWinner !== 'draw' && (
            <p className="text-lg font-black text-[#f5c518] tabular-nums mb-1">
              {m.home_score ?? 0} – {m.away_score ?? 0}
            </p>
          )}
          {isResultSport && mWinner && mWinner !== 'draw' && (
            <p className="text-sm text-[#8a93a3] mb-1">
              {mWinner === 'home' ? homeLabel : awayLabel} +2 pts
            </p>
          )}
          {mWinner === 'draw' && (
            <p className="text-sm text-[#8a93a3] mb-1">1 pt each</p>
          )}

          <p className="text-xs text-[#22c55e] font-semibold mb-8">✓ Points awarded · Scoreboard updated</p>

          <div className="flex flex-col gap-3">
            {hasNext && (
              <button data-testid="next-match-btn"
                onClick={() => { setFocusTie(nextIdx); setMatchSaved(false); }}
                className="w-full h-14 rounded-xl bg-[#f5c518] text-[#0b0f18] font-black text-lg hover:bg-[#f7d24a] transition-colors flex items-center justify-center gap-2">
                Next Match
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            )}
            <button
              onClick={() => navigate(-1)}
              className="w-full h-12 rounded-xl border border-[#252a37] text-[#f0f2f7] font-semibold hover:bg-[#161a26] transition-colors">
              Back to Tie
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Result overlay — shown after "End Match"
  if (showResult) {
    const winnerLabel = sportWinner === 'home' ? homeLabel
                      : sportWinner === 'away' ? awayLabel
                      : 'Draw';
    const winnerColor = sportWinner === 'home' ? homeColor
                      : sportWinner === 'away' ? awayColor
                      : '#f5c518';
    return (
      <div className="fixed inset-0 bg-[#06080d] flex flex-col items-center justify-center p-6 z-50">
        <div className="text-center max-w-sm">
          <SportIcon sport={sportCode} size={48}/>
          <p className="text-sm text-[#8a93a3] uppercase tracking-widest mb-2">{SPORT_LABELS[sportCode]} Result</p>
          <div className="text-4xl font-black mb-1" style={{color: winnerColor}}>
            {winnerLabel || 'Draw'} wins
          </div>
          <div className="text-2xl font-black text-[#f5c518] mb-2">
            {sportResult.home_match_wins||0} – {sportResult.away_match_wins||0}
          </div>
          <p className="text-[#8a93a3] text-sm mb-2">
            {homeLabel} <span className="text-[#f0f2f7] font-bold">{sportResult.home_pts||0}</span> pts &nbsp;·&nbsp;
            {awayLabel} <span className="text-[#f0f2f7] font-bold">{sportResult.away_pts||0}</span> pts
          </p>
          <p className="text-xs text-[#22c55e] mb-6">✓ Result saved · Scoreboard updated</p>
          <Button onClick={() => navigate('/score')}
            className="w-full h-12 bg-[#f5c518] text-[#0b0f18] font-black text-base">
            Back to All Matches
          </Button>
          <Button variant="ghost" onClick={() => setShowResult(false)}
            className="w-full mt-2 text-[#8a93a3]">
            Continue scoring
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-5 pb-28">
      {/* Back */}
      <Button variant="ghost" onClick={() => singleMatchMode ? navigate(-1) : navigate('/score')}
        className="mb-3 text-[#8a93a3] hover:text-[#f0f2f7] -ml-2">
        <ArrowLeft className="h-4 w-4 mr-2"/>{singleMatchMode ? 'Back to Tie' : 'Back to Matches'}
      </Button>

      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <SportIcon sport={sportCode} size={32}/>
        <div>
          <h1 className="text-xl font-black text-[#f0f2f7]">{SPORT_LABELS[sportCode]||sportCode}</h1>
          <p className="text-xs text-[#8a93a3]">
            <span style={{color:enc.home_team?.color||'#3b82f6'}}>{homeLabel}</span>
            {' vs '}
            <span style={{color:enc.away_team?.color||'#ef4444'}}>{awayLabel}</span>
            {enc.match_label && <span className="ml-2 font-mono text-[#8a93a3]">· {enc.match_label}</span>}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {singleMatchMode ? (
            /* Single-match: show "Match X of Y" */
            <span className="bg-[#1d2230] border border-[#252a37] text-[#f5c518] font-black text-sm px-3 py-1 rounded-lg">
              Match {focusTie + 1} <span className="text-[#8a93a3] font-normal">/ {sportResult.matches?.length || 0}</span>
            </span>
          ) : (
            <>
              <Badge className={`border text-xs ${sportResult.sport_winner?'bg-[rgba(34,197,94,0.12)] text-[#22c55e] border-[rgba(34,197,94,0.3)]':'bg-[#1d2230] text-[#8a93a3] border-[#252a37]'}`}>
                {sportResult.sport_winner ? `${sportResult.home_match_wins}–${sportResult.away_match_wins} Done` : `${sportResult.home_match_wins||0}–${sportResult.away_match_wins||0}`}
              </Badge>
              <span className="text-[10px] text-[#8a93a3] bg-[#1d2230] px-2 py-0.5 rounded border border-[#252a37]">{sportDef.label}</span>
            </>
          )}
        </div>
      </div>

      {/* Time row — only in full mode */}
      {!singleMatchMode && (
      <div className="flex items-center gap-5 mb-4 text-xs text-[#8a93a3] px-1">
        {['start_time','end_time'].map(field=>(
          <div key={field} className="flex items-center gap-1.5">
            {field==='start_time'?<Play className="h-3 w-3"/>:<Square className="h-3 w-3"/>}
            {field==='start_time'?'Start':'End'}:
            {editingTime===field ? (
              <span className="flex items-center gap-1">
                <Input type="time" value={editTimeVal} onChange={e=>setEditTimeVal(e.target.value)}
                  className="h-6 w-24 bg-[#1d2230] border-[#252a37] text-[#f5c518] text-xs px-1"/>
                <button onClick={()=>saveEditedTime(field)} className="text-[#22c55e]"><Check className="h-3.5 w-3.5"/></button>
                <button onClick={()=>setEditingTime(null)} className="text-[#8a93a3]">×</button>
              </span>
            ) : (
              <button onClick={()=>{setEditingTime(field);setEditTimeVal(enc[field]?new Date(enc[field]).toTimeString().slice(0,5):new Date().toTimeString().slice(0,5));}}
                className="flex items-center gap-1 group">
                <strong className={enc[field]?field==='end_time'?'text-[#22c55e]':'text-[#f0f2f7]':'text-[#8a93a3]'}>
                  {enc[field]?fmtTime(enc[field]):'—'}
                </strong>
                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-60"/>
              </button>
            )}
          </div>
        ))}
      </div>
      )}

      {/* Match cards */}
      <div className="space-y-2">
        {(sportResult.matches||[]).map((m, idx) => {
          // In single-match mode, only render the focused match
          if (singleMatchMode && idx !== focusTie) return null;

          const totalMatches = sportResult.matches.length;
          // Last match = platinum for singles sports; last 2 = platinum for doubles with 3+ matches
          // isDoubles: check explicit type field first (case-insensitive)
          // Fallback: sports with singles_count=0 are doubles-only (Badminton, Carrom, Pickleball)
          const sportIsAllDoubles = (sportFmt?.singles_count ?? sportDef?.singles_count ?? 1) === 0;
          const isDoubles = m.type?.toLowerCase() === 'doubles' || (!m.type && sportIsAllDoubles);
          // Platinum match count = platinum_count ÷ players per match
          // Singles: 1 player/match → platMatchCount = platinum_count
          // Doubles: 2 players/match → platMatchCount = platinum_count ÷ 2
          const platCount    = sportFmt?.platinum_count ?? (isDoubles ? 2 : 2);
          const platMatches  = isDoubles ? Math.max(1, Math.floor(platCount / 2)) : platCount;
          // Platinum slots: first N matches (P1, P2...) — NOT last N
          const isPlatByIndex = idx < platMatches;
          const isPlatSlot   = isPlatByIndex
            || m.tier === 'platinum'
            || (m.slot && m.slot.startsWith('P'))
            || (m.label && m.label.startsWith('P'));
          const sc = isPlatSlot ? '#f5c518' : (isDoubles ? '#3b82f6' : '#8b5cf6');

          // Match combination type for Ace/Champion tournaments
          // Inferred from slot label (CC, AA, AC) or standard 5-doubles order
          const COMBO_ORDER = ['CC','AA','CC','AC','AC']; // standard 5-doubles badminton
          const getComboType = () => {
            if (m.combo) return m.combo; // explicit combo field
            const sl = (m.slot||m.label||'').toUpperCase();
            if (['CC','AA','AC','CA'].includes(sl)) return sl;
            // infer from position for 5-doubles
            if (totalMatches === 5 && isDoubles) return COMBO_ORDER[idx] || null;
            return null;
          };
          const comboType = getComboType();
          const COMBO_COLORS = { CC:'#22c55e', AA:'#f5c518', AC:'#3b82f6', CA:'#3b82f6' };
          const COMBO_LABELS = { CC:'Champion + Champion', AA:'Ace + Ace', AC:'Ace + Champion', CA:'Champion + Ace' };

          // For Ace/Champion categorised tournaments, filter by combo type
          const isAceRequired  = (side) => comboType === 'AA' || (comboType === 'AC' && side === 'home') || (comboType === 'CA' && side === 'away');
          const isChampRequired = (side) => comboType === 'CC' || (comboType === 'AC' && side === 'away') || (comboType === 'CA' && side === 'home');

          const getCategoryPool = (side) => {
            const allPool = getPlayers(side);
            if (!comboType || isSubstitute) return allPool;
            if (comboType === 'AA') return allPool.filter(p => p.category === 'ace' || p.platinum_sports?.includes(sportCode) || p.is_platinum);
            if (comboType === 'CC') return allPool.filter(p => p.category === 'champion' || (!p.is_platinum && !p.platinum_sports?.includes(sportCode)));
            if (comboType === 'AC') {
              if (side === 'home') return allPool.filter(p => p.category === 'ace' || p.is_platinum);
              return allPool.filter(p => p.category === 'champion' || (!p.is_platinum));
            }
            return allPool; // fallback
          };

          const homePl  = getMatchValue(idx,'home_player', m.home_player||'');
          const awayPl  = getMatchValue(idx,'away_player', m.away_player||'');
          const homePl2 = getMatchValue(idx,'home_player2', m.home_player2||m.home_players?.[1]||'');
          const awayPl2 = getMatchValue(idx,'away_player2', m.away_player2||m.away_players?.[1]||'');
          const homeS   = getMatchValue(idx,'home_score', m.home_score??'');
          const awayS   = getMatchValue(idx,'away_score', m.away_score??'');
          const winner  = getMatchValue(idx,'winner', m.winner);

          const allHomePlayers = getPlayers('home');
          const allAwayPlayers = getPlayers('away');
          const subKey = `sub_${idx}`;
          const isSubstitute = !!dirtyMatches[subKey]?.substitute;
          // Use category-filtered pool when combo type is known, else platinum logic
          const homePlayers = comboType && !isSubstitute ? getCategoryPool('home') : ((isPlatSlot && !isSubstitute) ? getPlatinumPlayers('home') : allHomePlayers);
          const awayPlayers = comboType && !isSubstitute ? getCategoryPool('away') : ((isPlatSlot && !isSubstitute) ? getPlatinumPlayers('away') : allAwayPlayers);

          // Inline helper to build player option list (avoids component-in-render anti-pattern)
          const makeOptions = (side, excluded) => {
            const pool = (isPlatSlot && !isSubstitute)
              ? (side === 'home' ? homePlayers : awayPlayers)
              : (side === 'home' ? allHomePlayers : allAwayPlayers);
            const available = pool.filter(p => p.player_name !== excluded);
            return (
              <>
                {available.map(p => (
                  <option key={p.player_id} value={p.player_name}>
                    {p.platinum_sports?.includes(sportCode) ? '★ ' : ''}{p.player_name}
                  </option>
                ))}
                {available.length === 0 && isPlatSlot && !isSubstitute && (
                  <option disabled>No platinum players — mark platinum on team page</option>
                )}
              </>
            );
          };
          const selClass = "flex-1 bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] text-xs rounded-lg px-2 py-2 focus:outline-none focus:border-[#f5c518]";

          return (
            
<Card key={m.slot || m.label || idx} className={`border overflow-hidden ${winner?'bg-[#0f1219] border-[#1e2435]':'bg-[#161a26] border-[#252a37]'} ${isPlatSlot?'ring-1 ring-[rgba(245,197,24,0.2)]':''}`}>
              <CardContent className="p-3">
                {/* Tie header */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base font-black text-[#f5c518] w-7 text-center tabular-nums">#{idx+1}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                    style={{backgroundColor:`${sc}20`,color:sc}}>{m.slot||m.label||`M${idx+1}`}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1d2230] text-[#8a93a3] border border-[#252a37]">
                    {isDoubles ? 'Doubles' : 'Singles'}
                  </span>
                  {isPlatSlot && (
                    <span className="text-xs font-bold text-[#f5c518] bg-[rgba(245,197,24,0.1)] px-2 py-0.5 rounded-full border border-[rgba(245,197,24,0.3)]">
                      ★ Platinum match
                    </span>
                  )}
                  {/* Combination type badge */}
                  {comboType && (
                    <span className="text-[10px] font-black px-2 py-0.5 rounded-full border flex-shrink-0"
                      style={{ color: COMBO_COLORS[comboType]||'#8a93a3', borderColor:`${COMBO_COLORS[comboType]||'#8a93a3'}40`, backgroundColor:`${COMBO_COLORS[comboType]||'#8a93a3'}12` }}>
                      {comboType} · {COMBO_LABELS[comboType] || comboType}
                    </span>
                  )}
                  {winner && (
                    <span className={`text-xs font-semibold ml-auto ${winner==='home'||winner==='bye_home'?'text-[#22c55e]':'text-[#ef4444]'}`}>
                      {winner==='home'||winner==='bye_home'?`${homeLabel} wins`:`${awayLabel} wins`}
                      {(winner==='bye_home'||winner==='bye_away')&&' (BYE)'}
                    </span>
                  )}
                </div>
                {/* Substitute toggle — separate row so it's always visible on mobile */}
                {isPlatSlot && (
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={() => setDirtyMatches(prev => ({
                        ...prev,
                        [subKey]: { ...prev[subKey], substitute: !isSubstitute },
                      }))}
                      className={`text-[11px] font-bold px-3 py-1.5 rounded-full border transition-colors ${
                        isSubstitute
                          ? 'bg-[rgba(239,68,68,0.12)] border-[rgba(239,68,68,0.4)] text-[#ef4444]'
                          : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-[rgba(239,68,68,0.4)] hover:text-[#ef4444]'
                      }`}
                      title="Platinum player absent — use substitute from full pool"
                    >
                      {isSubstitute ? '⚠ Substitute ON' : 'Platinum absent? →'}
                    </button>
                  </div>
                )}

                {/* Player selection — inline selects (always editable, player names are metadata) */}
                <div className="space-y-2 mb-3">
                  {/* P1 row */}
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                    <select value={homePl} onChange={e=>updateField(idx,'home_player',e.target.value)} className={selClass}>
                      <option value="">{isDoubles?`${homeLabel} — P1`:`${homeLabel} player`}</option>
                      {makeOptions('home', '')}
                    </select>
                    <span className="text-[#8a93a3] text-xs font-bold">vs</span>
                    <select value={awayPl} onChange={e=>updateField(idx,'away_player',e.target.value)} className={selClass}>
                      <option value="">{isDoubles?`${awayLabel} — P1`:`${awayLabel} player`}</option>
                      {makeOptions('away', '')}
                    </select>
                  </div>
                  {/* P2 row (doubles only) */}
                  {isDoubles && (
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                      <select value={homePl2} onChange={e=>updateField(idx,'home_player2',e.target.value)} className={selClass}>
                        <option value="">{homeLabel} — P2</option>
                        {makeOptions('home', homePl)}
                      </select>
                      <span className="text-[#8a93a3] text-[10px]">&amp;</span>
                      <select value={awayPl2} onChange={e=>updateField(idx,'away_player2',e.target.value)} className={selClass}>
                        <option value="">{awayLabel} — P2</option>
                        {makeOptions('away', awayPl)}
                      </select>
                    </div>
                  )}
                </div>

                {/* Score entry — game-by-game for TT, single score for others */}
                {needsGames ? (
                  /* Game-by-game rows (TT: best of 3/5) */
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-[10px] text-[#8a93a3] px-1 mb-1">
                      <span className="w-12 text-center">Game</span>
                      <span className="flex-1 text-center" style={{color:enc?.home_team?.color||'#3b82f6'}}>{homeLabel}</span>
                      <span className="w-6"/>
                      <span className="flex-1 text-center" style={{color:enc?.away_team?.color||'#ef4444'}}>{awayLabel}</span>
                      <span className="w-6"/>
                    </div>
                    {Array.from({length: gamesPerMatch}, (_, gi) => {
                      const existingGames = (dirtyMatches[idx]?.games || m.games || []);
                      const g = existingGames[gi] || { home:0, away:0 };
                      const rubber_hw = (dirtyMatches[idx]?.home_score ?? m.home_score ?? 0);
                      const rubber_aw = (dirtyMatches[idx]?.away_score ?? m.away_score ?? 0);
                      const rubberDone = rubber_hw >= toWinGames || rubber_aw >= toWinGames;
                      // Disable game rows beyond what's needed
                      const isNeeded = !rubberDone || (g.home > 0 || g.away > 0);
                      const gp = sportFmt?.game_points ?? 11;
                      const minDiff = sportFmt?.min_diff ?? 2;
                      const winTarget = Math.max(gp, Math.max(g.home||0, g.away||0));
                      const gWinner = (g.home>=winTarget && g.home-g.away>=minDiff)?'home':(g.away>=winTarget && g.away-g.home>=minDiff)?'away':null;
                      return (
                        <div key={gi} className={`grid grid-cols-[3rem_1fr_2rem_1fr_1.5rem] gap-2 items-center ${!isNeeded?'opacity-30':''}`}>
                          <span className={`text-xs font-bold text-center px-1.5 py-0.5 rounded ${gWinner?'bg-[rgba(245,197,24,0.1)] text-[#f5c518]':'bg-[#1d2230] text-[#8a93a3]'}`}>
                            G{gi+1}
                          </span>
                          <Input type="number" min="0" max={gp+10} value={g.home||''}
                            onChange={e => updateGame(idx, gi, 'home', e.target.value)}
                            disabled={!isNeeded}
                            placeholder={String(gp)}
                            className={`h-10 text-center text-lg font-black bg-[#1d2230] border-[#252a37] focus:border-[#f5c518] focus:outline-none ${gWinner==='home'?'text-[#22c55e] border-[rgba(34,197,94,0.4)]':gWinner==='away'?'text-[#ef4444]/60':'text-[#f0f2f7]'}`}/>
                          <span className="text-[#8a93a3] text-center font-bold">:</span>
                          <Input type="number" min="0" max={gp+10} value={g.away||''}
                            onChange={e => updateGame(idx, gi, 'away', e.target.value)}
                            disabled={!isNeeded}
                            placeholder={String(gp)}
                            className={`h-10 text-center text-lg font-black bg-[#1d2230] border-[#252a37] focus:border-[#f5c518] focus:outline-none ${gWinner==='away'?'text-[#22c55e] border-[rgba(34,197,94,0.4)]':gWinner==='home'?'text-[#ef4444]/60':'text-[#f0f2f7]'}`}/>
                          <span className="text-xs text-center font-bold">
                            {gWinner==='home'?<span className="text-[#22c55e]">H</span>:gWinner==='away'?<span className="text-[#22c55e]">A</span>:null}
                          </span>
                        </div>
                      );
                    })}
                    {/* Rubber summary */}
                    {(dirtyMatches[idx]?.winner || m.winner) && (
                      <div className="text-center mt-1">
                        <span className={`text-sm font-black ${(dirtyMatches[idx]?.winner||m.winner)==='home'?'text-[#22c55e]':'text-[#ef4444]'}`}>
                          {(dirtyMatches[idx]?.winner||m.winner)==='home'?homeLabel:awayLabel} wins rubber
                          ({dirtyMatches[idx]?.home_score??m.home_score??0} – {dirtyMatches[idx]?.away_score??m.away_score??0} games)
                        </span>
                      </div>
                    )}
                  </div>
                ) : isResult ? (
                  /* Chess/Carrom: Win/Draw/Loss with auto-points */
                  <div className="space-y-2">
                    <div className="flex gap-1.5">
                      {[
                        {w:'home', label:`${homeLabel} Wins`, color:'#22c55e', pts:`${homeLabel} +2 pts`, hs:2, as:0},
                        {w:'draw', label:'Draw',             color:'#f5c518', pts:'1 pt each',           hs:1, as:1},
                        {w:'away', label:`${awayLabel} Wins`,color:'#ef4444', pts:`${awayLabel} +2 pts`, hs:0, as:2},
                      ].map(opt => (
                        <button key={opt.w}
                          onClick={() => {
                            if (winner === opt.w) {
                              updateField(idx,'winner',null);
                              updateField(idx,'home_score',0);
                              updateField(idx,'away_score',0);
                            } else {
                              updateField(idx,'winner',opt.w);
                              updateField(idx,'home_score',opt.hs);
                              updateField(idx,'away_score',opt.as);
                            }
                          }}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-colors disabled:opacity-40 ${
                            winner===opt.w
                              ? 'border-current text-current bg-current/10'
                              : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:text-current hover:border-current'
                          }`}
                          style={{color:winner===opt.w?opt.color:undefined, borderColor:winner===opt.w?opt.color:undefined}}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {/* Points summary */}
                    {winner && (
                      <div className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-semibold ${
                        winner==='draw' ? 'bg-[rgba(245,197,24,0.08)] border border-[rgba(245,197,24,0.25)]' :
                        winner==='home' ? 'bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.25)]' :
                                          'bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.25)]'
                      }`}>
                        <span style={{color: winner==='home'?'#22c55e':winner==='away'?'#ef4444':'#f5c518'}}>
                          {winner==='home' ? `🏆 ${homeLabel}` : winner==='away' ? `🏆 ${awayLabel}` : '🤝 Draw'}
                        </span>
                        <div className="flex items-center gap-3 text-xs">
                          <span className={winner==='home'?'text-[#22c55e] font-black':'text-[#8a93a3]'}>
                            {homeLabel.split(' ')[0]}: {winner==='home'?2:winner==='draw'?1:0} pts
                          </span>
                          <span className="text-[#8a93a3]">·</span>
                          <span className={winner==='away'?'text-[#22c55e] font-black':'text-[#8a93a3]'}>
                            {awayLabel.split(' ')[0]}: {winner==='away'?2:winner==='draw'?1:0} pts
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Single-score: Pickleball (15), Badminton (21), Squash */
                  (() => {
                    // Compute display winner from current scores (covers legacy data + new entries)
                    const computedW = !isResult && (() => {
                      const hs = parseInt(homeS) || 0;
                      const as_ = parseInt(awayS) || 0;
                      const gp = sportFmt?.game_points ?? sportDef.game_points ?? 11;
                      const md = sportFmt?.min_diff ?? 2;
                      if (hs >= gp && hs - as_ >= md) return 'home';
                      if (as_ >= gp && as_ - hs >= md) return 'away';
                      return null;
                    })();
                    const dispWinner = winner || computedW;

                    // Quick win button handler — sets winner + pre-fills score if blank
                    const handleQuickWin = (side) => {
                      const gp = sportFmt?.game_points ?? sportDef.game_points ?? 21;
                      const hs = parseInt(homeS) || 0;
                      const as_ = parseInt(awayS) || 0;
                      if (dispWinner === side) {
                        // Toggle off
                        setDirtyMatches(prev => ({
                          ...prev,
                          [idx]: { ...(prev[idx] || {}), winner: null, home_score: '', away_score: '' }
                        }));
                      } else {
                        setDirtyMatches(prev => ({
                          ...prev,
                          [idx]: {
                            ...(prev[idx] || {}),
                            winner: side,
                            home_score: side === 'home' ? (hs >= gp ? hs : gp) : (hs > 0 ? hs : 0),
                            away_score: side === 'away' ? (as_ >= gp ? as_ : gp) : (as_ > 0 ? as_ : 0),
                          }
                        }));
                      }
                    };

                    return (
                  <div className="space-y-2">
                    {/* Quick Win / Loss buttons */}
                    <div className="flex gap-1.5">
                      {[
                        { w: 'home', label: `${homeLabel} Wins`, color: '#22c55e' },
                        { w: 'away', label: `${awayLabel} Wins`, color: '#ef4444' },
                      ].map(opt => (
                        <button key={opt.w}
                          onClick={() => handleQuickWin(opt.w)}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-colors disabled:opacity-40 ${
                            dispWinner === opt.w
                              ? 'border-current text-current bg-current/10'
                              : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:text-current hover:border-current'
                          }`}
                          style={{
                            color: dispWinner === opt.w ? opt.color : undefined,
                            borderColor: dispWinner === opt.w ? opt.color : undefined,
                          }}>
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {/* Optional exact score entry */}
                    <p className="text-[10px] text-[#8a93a3] text-center">or enter exact score</p>
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                      <Input type="number" min="0" max={sportDef.game_points+10} value={homeS}
                        onChange={e=>updateField(idx,'home_score',e.target.value)}
                        placeholder={String(sportDef.game_points)}
                        className={`h-14 text-center text-3xl font-black bg-[#1d2230] focus:outline-none transition-colors ${
                          dispWinner==='home'
                            ? 'border-2 border-[#22c55e] text-[#22c55e]'
                            : dispWinner==='away'
                            ? 'border border-[#252a37] text-[#f0f2f7]/50'
                            : 'border border-[#252a37] text-[#f0f2f7] focus:border-[#f5c518]'
                        }`}/>
                      <span className="text-[#8a93a3] font-bold text-xl px-1">:</span>
                      <Input type="number" min="0" max={sportDef.game_points+10} value={awayS}
                        onChange={e=>updateField(idx,'away_score',e.target.value)}
                        placeholder={String(sportDef.game_points)}
                        className={`h-14 text-center text-3xl font-black bg-[#1d2230] focus:outline-none transition-colors ${
                          dispWinner==='away'
                            ? 'border-2 border-[#22c55e] text-[#22c55e]'
                            : dispWinner==='home'
                            ? 'border border-[#252a37] text-[#f0f2f7]/50'
                            : 'border border-[#252a37] text-[#f0f2f7] focus:border-[#f5c518]'
                        }`}/>
                    </div>
                    {/* Target score hint */}
                    <p className="text-center text-[10px] text-[#8a93a3]">
                      First to <strong className="text-[#f5c518]">{sportDef.game_points}</strong> pts
                      {(sportFmt?.min_diff ?? 2) > 1 && ` (win by ${sportFmt?.min_diff ?? 2})`}
                    </p>
                    {/* Winner banner */}
                    {dispWinner && !dispWinner.startsWith('bye') && (
                      <div className="flex items-center justify-center gap-2 py-2.5 rounded-xl font-black text-sm bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.4)] text-[#22c55e]">
                        🏆 {dispWinner==='home' ? homeLabel : awayLabel} wins!
                      </div>
                    )}
                  </div>
                    );
                  })()
                )}

                {/* BYE buttons */}
                {!isDone && !winner && (
                  <div className="flex gap-2 mt-2">
                    {[{side:'home',label:homeLabel},{side:'away',label:awayLabel}].map(opt=>(
                      <button key={opt.side}
                        onClick={()=>{
                          updateField(idx,'winner',`bye_${opt.side}`);
                          updateField(idx,'home_score',opt.side==='home'?2:0);
                          updateField(idx,'away_score',opt.side==='away'?2:0);
                        }}
                        className="flex-1 py-1.5 rounded-lg text-xs font-semibold border border-[rgba(245,197,24,0.25)] bg-[rgba(245,197,24,0.06)] text-[#f5c518] hover:bg-[rgba(245,197,24,0.12)] transition-colors">
                        {opt.label} BYE (absent)
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Golden game card — full mode, shown when TT matches are all done and tied */}
      {showGoldenGameCard && (
        <Card className="border-2 border-[rgba(245,197,24,0.45)] bg-[rgba(245,197,24,0.03)] ring-1 ring-[rgba(245,197,24,0.1)] mt-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[#f5c518] font-black text-xs px-2 py-0.5 bg-[rgba(245,197,24,0.15)] rounded-lg border border-[rgba(245,197,24,0.35)]">
                ⚡ GOLDEN GAME
              </span>
              <span className="text-[10px] text-[#8a93a3]">No points · Decides tie winner</span>
              {bonusGame?.winner && <span className="text-[10px] text-[#22c55e] font-bold ml-auto">✓ Saved</span>}
            </div>
            <p className="text-xs text-[#8a93a3] mb-3">
              Score is {cgHomeWins}–{cgAwayWins}. Play one bonus game to decide the tie winner.
            </p>
            <div className="flex gap-2">
              {[
                { side:'home', label:`${homeLabel} Wins`, color:'#22c55e' },
                { side:'away', label:`${awayLabel} Wins`, color:'#ef4444' },
              ].map(opt => (
                <button key={opt.side}
                  onClick={() => { setBonusGame({ winner: opt.side }); setBonusGameDirty(true); }}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-colors ${
                    bonusGame?.winner === opt.side
                      ? 'border-current text-current bg-current/10'
                      : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:text-current hover:border-current'
                  }`}
                  style={{ color: bonusGame?.winner===opt.side ? opt.color : undefined,
                           borderColor: bonusGame?.winner===opt.side ? opt.color : undefined }}>
                  {opt.label}
                </button>
              ))}
            </div>
            {bonusGame?.winner && (
              <div className="mt-2.5 text-center py-2 rounded-lg bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.25)]">
                <p className="text-[#22c55e] font-black text-sm">
                  ⚡ {bonusGame.winner==='home' ? homeLabel : awayLabel} wins the tie!
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#10131c]/95 backdrop-blur border-t border-[#252a37] p-4">
        <div className="flex flex-col gap-2 max-w-2xl mx-auto">

          {singleMatchMode ? (
            /* ── Single-match mode: one save button ── */
            (() => {
              const curMatch = sportResult?.matches?.[focusTie];
              const curWinner = getMatchValue(focusTie, 'winner', curMatch?.winner);
              const isDirty = !!dirtyMatches[focusTie];
              const alreadySaved = !!curMatch?.winner && !isDirty;
              return (
                <Button data-testid="save-match-btn"
                  onClick={handleSingleMatchSave}
                  disabled={saving || (!isDirty && alreadySaved)}
                  className={`w-full h-14 font-black text-base transition-colors ${
                    alreadySaved && !isDirty
                      ? 'bg-[#1d2230] border border-[rgba(34,197,94,0.3)] text-[#22c55e]'
                      : curWinner
                        ? 'bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]'
                        : 'bg-[#3b82f6] text-white hover:bg-[#2563eb]'
                  }`}>
                  {saving ? 'Saving...' :
                   alreadySaved && !isDirty ? '✓ Match Already Saved' :
                   curWinner ? 'Save Match Result' : 'Save Progress'}
                </Button>
              );
            })()
          ) : (
            /* ── Full mode: Start / Save / End ── */
            <>
              {!isLive && !isDone && (
                <Button onClick={handleStartMatch}
                  className="w-full h-12 bg-[#22c55e] hover:bg-[#16a34a] text-white font-black text-base">
                  <Play className="h-5 w-5 mr-2"/>Start Scoring
                </Button>
              )}
              {isLive && !isDone && (
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={saving||!hasDirty} variant="outline"
                    className={`flex-1 h-12 font-semibold ${hasDirty?'border-[#f5c518] text-[#f5c518] hover:bg-[rgba(245,197,24,0.08)]':'border-[#252a37] text-[#8a93a3]'}`}>
                    <Save className="h-4 w-4 mr-2"/>
                    {saving?'Saving...':hasDirty?`Save (${Object.keys(dirtyMatches).length} changes)`:'Saved'}
                  </Button>
                  <Button onClick={handleEndMatch} disabled={saving}
                    className={`flex-1 h-12 font-black text-base ${allTiesDone?'bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]':'bg-[#3b82f6] hover:bg-[#2563eb] text-white'}`}>
                    <Square className="h-4 w-4 mr-2"/>
                    {saving?'Ending...':'End Match'}
                  </Button>
                </div>
              )}
              {isDone && (
                <div className="space-y-2">
                  {/* Allow saving player name updates even on completed encounters */}
                  {hasDirty && (
                    <Button onClick={handleSave} disabled={saving}
                      className="w-full h-11 bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] font-black text-sm">
                      <Save className="h-4 w-4 mr-2"/>
                      {saving ? 'Saving…' : 'Save Changes'}
                    </Button>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#22c55e] font-semibold text-sm">✓ {SPORT_LABELS[sportCode]||sportCode} completed</p>
                      <p className="text-xs text-[#8a93a3]">
                        {sportWinner==='home'?homeLabel:sportWinner==='away'?awayLabel:'Draw'} wins ·
                        {' '}{sportResult.home_match_wins||0}–{sportResult.away_match_wins||0}
                      </p>
                    </div>
                    <Button onClick={() => setShowResult(true)} variant="outline"
                      className="border-[#252a37] text-[#f0f2f7] h-9 text-xs">
                      View Result
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
